import { useState, useEffect } from 'react';
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import { PaymentRequest } from '@stripe/stripe-js';

interface ApplePayButtonProps {
    price: number;
    label: string;
    onPaymentMethod: (ev: any) => void;
}

export function ApplePayButton({ price, label, onPaymentMethod }: ApplePayButtonProps) {
    const stripe = useStripe();
    const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
    const [canMakePayment, setCanMakePayment] = useState(false);

    useEffect(() => {
        if (!stripe) return;

        const pr = stripe.paymentRequest({
            country: 'FR',
            currency: 'eur',
            total: {
                label: label,
                amount: Math.max(1, Math.round(price * 100)), // Amount in cents, must be > 0
            },
            requestPayerName: true,
            requestPayerEmail: true,
            requestPayerPhone: true,
            requestShipping: true, // If physical goods
            shippingOptions: [
                {
                    id: 'standard',
                    label: 'Livraison',
                    detail: 'Livraison standard',
                    amount: 0,
                }
            ],
        });

        // Handle shipping address change to prevent errors
        pr.on('shippingaddresschange', (ev) => {
            ev.updateWith({
                status: 'success',
                shippingOptions: [
                    {
                        id: 'standard',
                        label: 'Livraison',
                        detail: 'Livraison standard',
                        amount: 0,
                    }
                ],
            });
        });

        // Check availability
        pr.canMakePayment().then((result) => {
            if (result) {
                setCanMakePayment(true);
                setPaymentRequest(pr);
            }
        });

        // Update payment request when price changes
        if (paymentRequest) {
            paymentRequest.update({
                total: {
                    label: label,
                    amount: Math.max(1, Math.round(price * 100)),
                },
            });
        }

    }, [stripe, price, label, onPaymentMethod]); // Re-run if price changes

    useEffect(() => {
        if (!paymentRequest) return;

        // Handle payment method creation
        // Handle payment method creation
        paymentRequest.on('paymentmethod', async (ev) => {
            if (onPaymentMethod) {
                // Delegate to parent (QuickBuyModal) to handle API calls
                try {
                    await onPaymentMethod(ev);
                    // Parent is responsible for calling ev.complete() or we assume success if promise resolves?
                    // Actually, if parent handles everything including ev.complete(), we just await.
                    // But if parent DOES NOT call ev.complete, we might hang.
                    // Best practice: Parent takes full control of `ev`.
                } catch (err) {
                    console.error("Payment handler error", err);
                    ev.complete('fail');
                }
            } else {
                // Fallback for demo
                ev.complete('success');
            }
        });

    }, [paymentRequest]);

    if (!canMakePayment || !paymentRequest) {
        // If not available (e.g. PC without Apple Pay), we return null (renders nothing)
        // OR we could render the 'placeholder' button if user wants to see it even if inactive?
        // User requested "Comment ça marcherait ce bouton... le faire".
        // It's better to show nothing if not supported to avoid broken UX.
        // But for dev/testing often we want to see it. 
        // I'll return null as per best practice.
        return null;
    }

    return (
        <div style={{ display: 'inline-block', height: '47px', width: '150px' }}>
            <PaymentRequestButtonElement
                options={{
                    paymentRequest,
                    style: {
                        paymentRequestButton: {
                            type: 'default', // 'default' | 'donate' | 'buy'
                            theme: 'dark', // Black theme matches design
                            height: '47px',
                        },
                    }
                }}
            />
        </div>
    );
}
